import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSocket } from '../contexts/SocketContext';

const InferenceMonitor = () => {
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, logsRes] = await Promise.all([
        axios.get('/api/inference/health'),
        axios.get('/api/inference/stats'),
        axios.get('/api/inference/logs?limit=20')
      ]);
      setHealth(healthRes.data.health);
      setStats(statsRes.data.stats);
      setLogs(logsRes.data.logs);
    } catch (error) {
      console.error('Failed to load inference data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (socket) {
      socket.on('inference:status', (data) => {
        setHealth(data);
      });
      return () => socket.off('inference:status');
    }
  }, [socket]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const local = health?.local || {};
  const runpod = health?.runpod || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Inference Monitor</h1>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] text-gray-300 rounded-xl transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Backend Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Local Ollama */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-2xl">
                🏠
              </div>
              <div>
                <h3 className="font-semibold text-lg">Local Ollama</h3>
                <p className="text-sm text-gray-500">Qwen 2.5 14B (Free)</p>
              </div>
            </div>
            <HealthIndicator healthy={local.healthy} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MetricBox label="Total Requests" value={local.metrics?.totalRequests || 0} />
            <MetricBox label="Avg Response" value={`${Math.round(local.metrics?.avgResponseTime || 0)}ms`} />
            <MetricBox label="Tokens/sec" value={(local.metrics?.tokensPerSecond || 0).toFixed(1)} />
            <MetricBox label="Queue Depth" value={local.metrics?.queueDepth || 0} highlight={local.metrics?.queueDepth > 0} />
          </div>

          {local.metrics?.lastCheck && (
            <p className="text-xs text-gray-500 mt-3">
              Last health check: {new Date(local.metrics.lastCheck).toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* RunPod */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl">
                ☁️
              </div>
              <div>
                <h3 className="font-semibold text-lg">RunPod A100</h3>
                <p className="text-sm text-gray-500">Qwen 2.5 72B ($0.0012/sec)</p>
              </div>
            </div>
            <HealthIndicator healthy={runpod.healthy} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MetricBox label="Total Requests" value={runpod.metrics?.totalRequests || 0} />
            <MetricBox label="Avg Response" value={`${Math.round(runpod.metrics?.avgResponseTime || 0)}ms`} />
            <MetricBox label="Cold Starts" value={runpod.metrics?.coldStarts || 0} />
            <MetricBox
              label="Total Cost"
              value={`$${(runpod.metrics?.totalCost || 0).toFixed(2)}`}
              highlight={true}
            />
          </div>

          {runpod.coldStart && (
            <div className="mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400">Cold start expected - no recent requests. First request may take 30s+.</p>
            </div>
          )}
        </div>
      </div>

      {/* Routing Performance */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <OverviewCard
          label="Local Requests"
          value={stats?.local?.totalRequests || 0}
          icon="🏠"
          color="text-emerald-400"
        />
        <OverviewCard
          label="RunPod Requests"
          value={stats?.runpod?.totalRequests || 0}
          icon="☁️"
          color="text-indigo-400"
        />
        <OverviewCard
          label="Local Avg Time"
          value={`${Math.round(stats?.local?.avgResponseTime || 0)}ms`}
          icon="⚡"
          color="text-amber-400"
        />
        <OverviewCard
          label="RunPod Avg Time"
          value={`${Math.round(stats?.runpod?.avgResponseTime || 0)}ms`}
          icon="⏱️"
          color="text-purple-400"
        />
      </div>

      {/* Live Request Feed */}
      <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Requests</h3>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No inference requests yet</p>
          ) : (
            logs.map((log) => (
              <div
                key={log._id}
                className="flex items-center gap-4 p-3 bg-[#0a0a0f] rounded-xl border border-[#2d2d3d] hover:border-indigo-500/30 transition-colors"
              >
                <div className={`w-2 h-2 rounded-full ${getStatusDot(log.status)}`} />

                <span className={`px-2 py-0.5 rounded text-xs font-medium min-w-[60px] text-center ${getBackendBadge(log.backend)}`}>
                  {log.backend}
                </span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{log.model}</span>
                    {log.agentId && (
                      <span className="text-xs text-gray-500">via {log.agentId.name}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{log.routingReason}</p>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="text-sm">{log.tokens?.total || 0} tokens</p>
                  <p className="text-xs text-gray-500">
                    {log.processingTime}ms • ${(log.cost || 0).toFixed(4)}
                  </p>
                </div>

                <span className="text-xs text-gray-500 flex-shrink-0">
                  {new Date(log.createdAt).toLocaleTimeString()}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const HealthIndicator = ({ healthy }) => (
  <div className="flex items-center gap-2">
    <div className={`w-4 h-4 rounded-full ${healthy ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
    <span className={`text-sm font-medium ${healthy ? 'text-green-400' : 'text-red-400'}`}>
      {healthy ? 'Healthy' : 'Down'}
    </span>
  </div>
);

const MetricBox = ({ label, value, highlight }) => (
  <div className="bg-[#0a0a0f] rounded-lg p-3">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-lg font-bold ${highlight ? 'text-amber-400' : 'text-white'}`}>{value}</p>
  </div>
);

const OverviewCard = ({ label, value, icon, color }) => (
  <div className="bg-[#12121a] border border-[#2d2d3d] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="text-lg">{icon}</span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const getStatusDot = (status) => {
  const dots = {
    completed: 'bg-green-500',
    processing: 'bg-blue-500 animate-pulse',
    failed: 'bg-red-500',
    pending: 'bg-yellow-500'
  };
  return dots[status] || 'bg-gray-500';
};

const getBackendBadge = (backend) => {
  const badges = {
    local: 'bg-emerald-500/20 text-emerald-400',
    runpod: 'bg-indigo-500/20 text-indigo-400',
    openai: 'bg-amber-500/20 text-amber-400',
    anthropic: 'bg-pink-500/20 text-pink-400',
    cohere: 'bg-purple-500/20 text-purple-400'
  };
  return badges[backend] || 'bg-gray-500/20 text-gray-400';
};

export default InferenceMonitor;
